import { toAbsoluteUrl } from "./resolve-redirect-url";
import { appendCurrentUtmParams } from "./utm-params";

export interface ResolvePostSubmitRedirectArgs {
  /** `form.submitRedirectURL`, the destination configured on the form itself. */
  submitRedirectURL: string | undefined;
  /** The `back` param on the link that opened this form, set by a Redirect field chaining into it. */
  backUrl: string | undefined;
  /**
   * Destination `submit_form_response` chose for this response — `redirectTo` (an integration's link)
   * or `nextFormGroupPublicURL` (the next form in a public group).
   */
  serverRedirectUrl: string | undefined;
  /**
   * Join link for an appointment booked during this form, captured on the form store by an
   * `Appointment Booking` field. Callers pass it only when the form sets
   * `redirectToBookedAppointmentOnSubmit`.
   */
  bookedAppointmentUrl?: string;
}

/**
 * `back` is the one candidate sourced from the query string rather than org configuration, so it
 * keeps legacy's scheme handling: legacy assigned the raw value to `location.href`, which means a
 * scheme-less `back=example.com/done` resolved as a *relative path* and stayed on the portal.
 * Running it through `toAbsoluteUrl` would promote it to `https://example.com/done` and send the
 * enduser to an arbitrary host — a hole legacy did not have. An explicit scheme is passed through
 * unchanged, matching legacy, which validated nothing here.
 */
const normalizeBackUrl = (backUrl: string): string | undefined =>
  /^[a-z][a-z0-9+.-]*:/i.test(backUrl) || backUrl.startsWith("//") ? toAbsoluteUrl(backUrl) : backUrl;

/**
 * Picks where to send the enduser once their response is recorded.
 *
 * The order reproduces what legacy actually did, which is not what its comments claim. Legacy made
 * these as sequential *unguarded* `location.href` assignments in one synchronous block
 * (`hooks.tsx:1561-1593`) — `redirectTo`/`nextFormGroupPublicURL` inside the per-enduser loop, then
 * `back`, `submitRedirectURL`, and the booked-appointment link after it — so the last assignment is
 * the one the browser acted on. That makes the booked-appointment link the true top priority and
 * `submitRedirectURL` second, ahead of everything else. The comment above `redirectTo` calling itself
 * "highest priority" describes an intent the control flow never implemented; the runtime order is
 * preserved here deliberately, since that is the behavior forms in the field are configured against.
 *
 * Each org-configured candidate is normalized by `toAbsoluteUrl`, so a scheme-less value gains
 * `https://` and a `javascript:`/`data:` target is declined rather than followed. `back` instead uses
 * `normalizeBackUrl`, which keeps a scheme-less value relative — see the note on that function. A
 * candidate that fails to normalize is skipped rather than ending the search: the response is already
 * saved, so falling through to a lower-priority target — or to the thanks screen — is strictly better
 * than stranding the enduser.
 *
 * The winning target carries the current URL's campaign-attribution params, as legacy applied
 * `append_current_utm_params` to each of these destinations.
 */
export const resolvePostSubmitRedirect = ({
  submitRedirectURL,
  backUrl,
  serverRedirectUrl,
  bookedAppointmentUrl,
}: ResolvePostSubmitRedirectArgs): string | undefined => {
  const candidates: { value: string | undefined; normalize: (url: string) => string | undefined }[] = [
    { value: bookedAppointmentUrl, normalize: toAbsoluteUrl },
    { value: submitRedirectURL, normalize: toAbsoluteUrl },
    { value: backUrl, normalize: normalizeBackUrl },
    { value: serverRedirectUrl, normalize: toAbsoluteUrl },
  ];

  const resolved = candidates.reduce<string | undefined>(
    (found, { value, normalize }) => found ?? (value ? normalize(value) : undefined),
    undefined
  );

  return resolved ? appendCurrentUtmParams(resolved) : undefined;
};
